import { useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

/**
 * ConfirmDialog — prop-driven confirm modal consolidating the Util variants
 * (ConfirmModal, DeleteConfirmModal, DeleteConfirmInputModal,
 * DeleteConfirmRemarkModal).
 *
 * Props:
 * - isOpen, toggle:  modal visibility control
 * - title:           header text (default "Confirm")
 * - message:         body text / node
 * - confirmText:     confirm button label (default "Confirm")
 * - confirmColor:    confirm button color (default "primary"; use "danger"
 *                    for deletes)
 * - onConfirm:       called on confirm; receives the entered value when
 *                    requireInput / requireRemark is used
 * - isLoading:       shows a spinner in the confirm button and disables it
 * - requireInput:    optional { label, placeholder, validate } — text input
 *                    whose value must be non-empty (and pass validate, which
 *                    may return false or an error string) before confirming
 * - requireRemark:   optional { label } — textarea variant of the same
 */
const ConfirmDialog = ({
  isOpen,
  toggle,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  confirmColor = "primary",
  onConfirm,
  isLoading = false,
  requireInput,
  requireRemark,
}) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);

  const needsValue = Boolean(requireInput || requireRemark);
  const field = requireInput || requireRemark;

  const resetAndToggle = () => {
    setValue("");
    setError(null);
    toggle();
  };

  const handleConfirm = () => {
    if (needsValue) {
      const trimmed = value.trim();
      if (!trimmed) {
        setError(`${field.label || "This field"} is required`);
        return;
      }
      if (requireInput && typeof requireInput.validate === "function") {
        const result = requireInput.validate(trimmed);
        if (result !== true && result !== undefined) {
          setError(typeof result === "string" ? result : "Invalid value");
          return;
        }
      }
      setError(null);
      onConfirm(trimmed);
      return;
    }
    onConfirm();
  };

  return (
    <Modal isOpen={isOpen} toggle={resetAndToggle}>
      <ModalHeader toggle={resetAndToggle}>{title}</ModalHeader>
      <ModalBody>
        {message && <p className={needsValue ? "" : "mb-0"}>{message}</p>}
        {needsValue && (
          <FormGroup className="mb-0">
            {field.label && (
              <Label for="confirm-dialog-value">{field.label}</Label>
            )}
            <Input
              id="confirm-dialog-value"
              type={requireRemark ? "textarea" : "text"}
              rows={requireRemark ? 4 : undefined}
              placeholder={requireInput ? requireInput.placeholder : undefined}
              value={value}
              invalid={Boolean(error)}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          color="secondary"
          onClick={resetAndToggle}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color={confirmColor}
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading && <Spinner size="sm" className="mr-2" />}
          {confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmDialog;
